import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { useAppSelector } from "../../../../redux/hooks/redux";
import { invalidateContactsAndWorkforces } from "../../../../utils";
import { DetailedWorkforceOverview } from "../../../employees/components/DetailedWorkforceOverview";
import { performSmartContactCleanup, useLinkWorkforce } from "../hooks/useContacts";

type Props = {
  phoneNumber: string;
  onDone: () => void;
  initialFilter?: any;
};

function buildWorkforcePhonesPayload(phoneNumber: string) {
  const digits = phoneNumber.replace(/\D/g, "");
  let dialCode = "+1";
  let phone = digits;

  if (digits.startsWith("1") && digits.length === 11) {
    phone = digits.substring(1);
  }

  return [
    {
      countryCode: "US",
      dialCode,
      phone,
      phoneNumber: dialCode + phone,
      tag: "primary",
      isDefault: false,
    },
  ];
}

export function LinkWorkforcePickerContent({ phoneNumber, onDone, initialFilter = "all" }: Props) {
  const { showSnackbar } = useGlobalSnackbar();
  const queryClient = useQueryClient();
  const companyId = useAppSelector((state) => state.userState.company?.id);
  const linkWorkforceMutation = useLinkWorkforce();

  const contact = useMemo(
    () => ({
      uid: "",
      name: "",
      lastName: "",
      email: "",
      notes: "",
      phoneNumbers: [{ number: phoneNumber, label: "primary", isPrimary: true }],
    }),
    [phoneNumber],
  );

  const handleLink = (workforce: any) => {
    const phonesPayload = buildWorkforcePhonesPayload(phoneNumber);

    const existingPhones = (workforce.phoneNumbers || []).map((existing: any) => {
      const dialCode = existing.dialCode || "+1";
      const phone = String(existing.phone || "").replace(/\D/g, "");

      return {
        ...existing,
        dialCode,
        phone,
        phoneNumber: existing.phoneNumber || `${dialCode}${phone}`,
      };
    });

    const combinedPhones = [...phonesPayload];

    existingPhones.forEach((existing: any) => {
      const alreadyIncluded = combinedPhones.some((newPhone) => newPhone.phoneNumber === existing.phoneNumber || newPhone.phone === existing.phone);

      if (!alreadyIncluded) {
        combinedPhones.push(existing);
      }
    });

    linkWorkforceMutation.mutate(
      {
        workforceUid: workforce.uid,
        phoneNumbers: combinedPhones,
      },
      {
        onSuccess: async () => {
          await performSmartContactCleanup(phoneNumber, companyId, queryClient);
          await invalidateContactsAndWorkforces(queryClient);
          showSnackbar("Phone number linked to workforce member successfully", "success");
          onDone();
        },
        onError: (err: any) => {
          const errMsg = err.response?.data?.message || err.message || "Failed to link to workforce";

          showSnackbar(errMsg, "error");
        },
      },
    );
  };

  function buildWorkforcePhonesPayload(phoneNumber: string) {
    const digits = phoneNumber.replace(/\D/g, "");
    const dialCode = "+1";

    let phone = digits;

    if (digits.startsWith("1") && digits.length === 11) {
      phone = digits.substring(1);
    }

    return [
      {
        countryCode: "US",
        dialCode,
        phone,
        phoneNumber: `${dialCode}${phone}`,
        tag: "primary",
        isDefault: false,
      },
    ];
  }

  return (
    <DetailedWorkforceOverview
      contact={contact}
      onLinkContact={handleLink}
      initialFilter={initialFilter}
      hideFilterToggle={initialFilter !== "all"}
    />
  );
}
